/*
 * Copyright, AlpineReplay, Inc. 2021. All rights reserved.
 * Author: Slobodan Djordjevic
 */
import React, { useState, useEffect } from "react"
import moment from "moment"
import { Modal } from "trux"
import { v4 as uuidv4 } from 'uuid'
import localforage from "localforage"
import { useSelector } from "react-redux"
import { useVideoSuspenders } from "trux/dist/organisms/VideoPlayer"
import {
    SurveysDocument,
    useAddSurveyAnswerMutation,
    useSurveysLazyQuery
} from "../../generated/graphql"
import { InAppSurveyContent } from "./InAppSurveyContent"
import "./InAppSurvey.css"

localforage.config({
    name: "trace",
    version: 1.0,
})

const InAppSurvey = ({token}) => {

    const [open, setOpen] = useState(false)
    const [activeSurvey, setActiveSurvey] = useState(null)
    const [currentHighlight, setCurrentHighlight] = useState(null)
    const [highlightsWatched, setHighlightsWatched] = useState(0)
    const [timeSpent, setTimeSpent] = useState(0)
    const [customerId, setCustomerId] = useState('')

    let user = useSelector((state) => {
        return state.user.value
    })

    let team = useSelector((state) => {
        return state.team.value
    })

    const event = useSelector((state) => {
        return state.video.value.event
    })

    const tokenReady = token != null

    const [addSurveyAnswer] = useAddSurveyAnswerMutation({
        variables: {
            token: token,
        },
        refetchQueries: [SurveysDocument, "surveys"],
    })

    const [getSurveys] = useSurveysLazyQuery({
        variables: {
            token: token
        },
        skip: !tokenReady,
    })

    const { suspend, resume } = useVideoSuspenders()

    const chooseActiveSurvey = async (surveys) => {
        if (surveys && surveys.length) {
            for (let survey of surveys) {
                let periodPassed = false
                let momentsWatchedThreshold = true
                let timeSpentThreshold = true
                let value = await localforage.getItem(`last_survey_date`)
                if (!value) {
                    periodPassed = true
                } else {
                    let lastSurveyDate = moment(value).add(survey.settings?.repeat_period, "days")
                    if (lastSurveyDate.isBefore(moment())) {
                        periodPassed = true
                    }
                }
                momentsWatchedThreshold = survey.settings?.moments_watched_threshold ? highlightsWatched > survey.settings?.moments_watched_threshold : true
                timeSpentThreshold = survey.settings?.moments_watched_threshold ? timeSpent > survey.settings.time_spent_threshold : true

                let percentageThreshold = survey.settings?.coverage
                let randomNumber = Math.random()
                if (!activeSurvey &&
                    periodPassed &&
                    momentsWatchedThreshold &&
                    timeSpentThreshold &&
                    randomNumber < percentageThreshold
                ) {
                    setActiveSurvey(survey)
                    await localforage.setItem("last_survey_date", moment().format('MM/DD/YYYY'))
                    break
                }
            }
        }
    }

    const handleTimeWatchedChange = (e) => {
        setTimeSpent(e?.detail?.sessionTime)
    }

    useEffect(() => {
        if(activeSurvey) {
            setOpen(true)
        }
    }, [activeSurvey])

    useEffect(() => {
        localforage.getItem(`customer_id`).then(function (value) {

            if (!value) {
                let cid = uuidv4()
                setCustomerId(cid)
                localforage.setItem("customer_id", cid)
            } else {
                setCustomerId(value)
            }

            if(customerId) {
                getSurveys({
                    variables: {
                        client_id: customerId
                    }
                }).then((response) => {
                    chooseActiveSurvey(response.data?.surveys)
                })
            }
        })

    }, [highlightsWatched, timeSpent])

    useEffect(() => {
        if (event && open) {
            setTimeout(() => {
                suspend()
            }, 1000)
        }

    }, [open, event])

    useEffect(() => {
        document.addEventListener("trc-time-watched", handleTimeWatchedChange)

        return () => {
            window.removeEventListener("trc-time-watched", handleTimeWatchedChange)
        }

    }, [])

    useEffect(() => {
        if (event && currentHighlight !== event.game_clip_id) {
            setCurrentHighlight(event.game_clip_id)
            setHighlightsWatched(highlightsWatched + 1)
        }

    }, [event])

    const saveAnswer = (answer) => {
        let teamId = team ? team.team_id : null
        addSurveyAnswer({
            variables: {
                client_id: customerId,
                survey_rule_id: activeSurvey.rule_id,
                answer_id: answer,
                app_name: "web-soccer-teams",
                team_id: teamId
            },
            skip: !tokenReady || !customerId,
        }).then(result => {
            console.log('Result: ', result)
        })
    }

    const onClose = (e = null) => {
        setOpen(false)
        resume()
    }

    const postFeedback = (value) => {
        onClose()
        saveAnswer(value)
    }

    if (open && activeSurvey) {

        return (
            <Modal
                trkContext="Rate App"
                open={open}
                onShadeClick={() => {
                    onClose();
                    saveAnswer(null)
                }}
                className="rate-app"
            >
                <InAppSurveyContent
                    user={user}
                    onRate={(value) => {
                        postFeedback(value)
                    }}
                    onClose={() => {
                        onClose()
                        saveAnswer(null)
                    }}
                    activeSurvey={activeSurvey}
                />
            </Modal>
        )
    }

    return null
}

export default InAppSurvey
